import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { ScrollReveal } from '@/components/motion'
import { BRAND, halftone } from './halftone'

export interface StoryCardProps {
  company: string
  industry: string
  headline: string
  quote: string
  author: string
  authorTitle: string
  metric: { label: string; before: string; after: string }
  href: string
}

const monogram = (name: string) =>
  name
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

/**
 * Featured customer story for the FAQ aside: a halftone colour tile with the
 * customer monogram and its headline result (before → after), the story
 * headline, the quote and a button to the stories page. Right-aligned like
 * the reference's case card.
 */
export function StoryCard({ company, industry, headline, quote, author, authorTitle, metric, href }: StoryCardProps) {
  return (
    <ScrollReveal className="flex flex-col lg:items-end lg:text-right">
      <div
        aria-hidden
        className="relative flex aspect-[4/4.6] w-full flex-col justify-between p-6 text-white"
        style={{ backgroundColor: BRAND.blue }}
      >
        <span className="absolute inset-0" style={halftone(0.24, 6)} />
        <span className="relative flex items-start justify-between">
          <span className="flex h-14 w-14 items-center justify-center bg-white font-[family-name:var(--font-landing-display)] text-lg text-[var(--color-landing-accent)]">
            {monogram(company)}
          </span>
          <span className="bg-[#ff00f2] px-2 py-1 text-xs font-bold uppercase tracking-wide">{industry}</span>
        </span>
        <span className="relative text-left">
          <span className="block text-sm font-semibold uppercase tracking-wide text-white/80">{metric.label}</span>
          <span className="mt-2 flex items-baseline gap-3 font-[family-name:var(--font-landing-display)] uppercase leading-[1.3]">
            <span className="text-lg text-white/60 line-through decoration-2">{metric.before}</span>
            <ArrowRight className="h-5 w-5 shrink-0 self-center" />
            <span className="text-3xl">{metric.after}</span>
          </span>
        </span>
      </div>

      <p className="mt-6 text-sm font-bold uppercase tracking-wide text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
        {company}
      </p>
      <h3 className="mt-2 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.45] text-[var(--color-landing-gray-900)] dark:text-white">
        {headline}
      </h3>
      <blockquote className="landing-text-body mt-4 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
        "{quote}"
      </blockquote>
      <p className="mt-4 text-sm text-[var(--color-landing-gray-900)] dark:text-white">
        <span className="font-semibold">{author}</span>
        <span className="text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">, {authorTitle}</span>
      </p>
      <Link
        to={href}
        className="landing-cta landing-cta--solid landing-cta--md landing-btn-sweep mt-8 self-start lg:self-end"
      >
        Read the story
        <ArrowRight className="h-4 w-4" />
      </Link>
    </ScrollReveal>
  )
}

export default StoryCard
